/**
 * main.js — Global theme entry point
 * Budget: ≤10KB uncompressed
 * Cart API, section events, animation observer, utilities
 * Also serves as the ES module source for @theme/cart and @theme/utilities imports.
 */

/* ===== CART API ===== */
const cartAPI = {
  async get() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  },

  async add(items, sections, opts) {
    const body = { items: Array.isArray(items) ? items : [items] };
    if (Array.isArray(sections) && sections.length) {
      body.sections = sections.join(',');
    }
    const res = await fetch(window.theme.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.description || 'Failed to add to cart');
    }
    const data = await res.json();
    cartAPI._dispatch();

    // opts.suppressAutoOpen lets programmatic adds (e.g. free-gift
    // threshold auto-add) skip the ATC-drawer popup behavior.
    if (!opts || !opts.suppressAutoOpen) {
      const cartDrawer = document.querySelector('cart-drawer[data-atc-auto-open]');
      if (cartDrawer && cartDrawer.dataset.atcAutoOpen === 'true') {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    }

    return data;
  },

  async update(updates) {
    const res = await fetch(window.theme.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error('Failed to update cart');
    const data = await res.json();
    cartAPI._dispatch();
    return data;
  },

  async change(line, quantity, sections) {
    const body = { id: line, quantity };
    if (Array.isArray(sections) && sections.length) {
      body.sections = sections.join(',');
    }
    const res = await fetch(window.theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Failed to change cart item');
    const data = await res.json();
    cartAPI._dispatch();
    return data;
  },

  async updateDiscount(code, sections) {
    const body = { discount: code == null ? '' : String(code) };
    if (Array.isArray(sections) && sections.length) {
      body.sections = sections.join(',');
    }
    const res = await fetch(window.theme.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Failed to update cart discount');
    const data = await res.json();
    cartAPI._dispatch();
    return data;
  },

  _dispatch() {
    cartAPI.get().then(cart => {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    });
  }
};

window.theme.cart_api = cartAPI;

/* ===== CART API — ES module exports (for import map @theme/cart) ===== */
export const getCart = cartAPI.get.bind(cartAPI);
export const addToCart = cartAPI.add.bind(cartAPI);
export const updateCart = cartAPI.update.bind(cartAPI);
export const changeCartItem = cartAPI.change.bind(cartAPI);
export const updateCartDiscount = cartAPI.updateDiscount.bind(cartAPI);

/* ===== SCROLL ANIMATIONS =====
 * P7 (S40-B3): content is visible by default. The hidden state is opt-in —
 * this function measures each [data-reveal] element and applies .will-reveal
 * (the only thing component-animations.css hides) exclusively to elements that
 * start below the fold. Above-fold content gets no class and no observer, so it
 * is visible from first paint and stays visible. Every early return below
 * therefore leaves the page fully visible, as does JS being absent entirely.
 */
function initAnimations(root = document) {
  if (!window.theme.settings.animations) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Skip anything already in a managed state: .is-visible (revealed) or
  // .will-reveal (opted in and observed by an earlier run). Re-measuring an
  // already-hidden element would read its transformed position.
  const candidates = root.querySelectorAll('[data-reveal]:not(.is-visible):not(.will-reveal)');
  if (!candidates.length) return;

  // MEASURE FIRST, MUTATE SECOND. Applying .will-reveal can move later siblings
  // (the variant transforms shift the element), so every rect is read into an
  // array before any class is written. Never interleave the two passes.
  const foldY = window.innerHeight || document.documentElement.clientHeight;
  const belowFold = [];
  candidates.forEach(el => {
    // Strictly below: an element whose top sits exactly on the fold line is
    // treated as above it and left visible. Erring toward visible is always safe.
    if (el.getBoundingClientRect().top > foldY) belowFold.push(el);
  });
  if (!belowFold.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

  belowFold.forEach(el => {
    el.classList.add('will-reveal');
    observer.observe(el);
  });
}

/* ===== THEME EDITOR EVENTS ===== */
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => initAnimations(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    e.target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

/* ===== UTILITIES ===== */
export function debounce(fn, delay = 300) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
}

export function throttle(fn, limit = 100) {
  let throttled = false;
  return (...args) => {
    if (!throttled) { fn(...args); throttled = true; setTimeout(() => { throttled = false; }, limit); }
  };
}

export function fetchJSON(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    }
  });
}

export function onTransitionEnd(el) {
  return new Promise(resolve => {
    const handler = () => {
      el.removeEventListener('transitionend', handler);
      resolve();
    };
    el.addEventListener('transitionend', handler);
  });
}

export function formatMoney(cents, format) {
  const moneyFormat = format || window.theme?.money_format || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');

  return moneyFormat
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', parts[0])
    .replace('{{amount_with_comma_separator}}', parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1])
    .replace('{{amount_no_decimals_with_comma_separator}}', parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','));
}

export function decodeHTML(str) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

export function trapFocus(container) {
  const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusableElements = container.querySelectorAll(focusableSelectors);
  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handler);
  first?.focus();

  return () => container.removeEventListener('keydown', handler);
}

export const pubsub = {
  _events: {},

  subscribe(event, callback) {
    if (!this._events[event]) this._events[event] = [];
    this._events[event].push(callback);
    return () => {
      this._events[event] = this._events[event].filter(cb => cb !== callback);
    };
  },

  publish(event, data) {
    if (!this._events[event]) return;
    this._events[event].forEach(cb => cb(data));
  }
};

export function announce(message) {
  const el = document.getElementById('a11y-live-region');
  if (!el) return;
  el.textContent = '';
  setTimeout(() => { el.textContent = message; }, 0);
}

window.theme.utilities = { debounce, throttle, fetchJSON, onTransitionEnd, formatMoney, decodeHTML, trapFocus, pubsub, announce };

/* ===== INIT ===== */
document.documentElement.classList.replace('no-js', 'js');
initAnimations();
